// Office-facing global queue: all variations across all jobs, filterable by status.
import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/pages/dashboard/_shared";
import { FileSignature } from "lucide-react";
import api from "@/lib/api";
import { VariationCard } from "@/components/VariationCard";

const TABS = [
  { key: "requested", label: "Awaiting price" },
  { key: "approved", label: "Approved" },
  { key: "invoiced", label: "Invoiced" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

export default function Variations() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("requested");
  const [loading, setLoading] = useState(true);
  const canEdit = ["admin", "office", "super_admin"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/variations", { params: { status: tab } });
      setItems(data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const counts = useMemo(() => {
    const c = { requested: 0, approved: 0, invoiced: 0, rejected: 0, all: items.length };
    items.forEach((v) => { c[v.status] = (c[v.status] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <div data-testid="variations-page" className="px-4 py-4 md:p-6 lg:p-10 md:max-w-7xl md:mx-auto pb-24 md:pb-10">
      <PageHeader
        eyebrow="/ Site extras"
        title="Variations"
        subtitle={
          canEdit
            ? "Signed requests for additional work from your customers. Price them up, approve, then mark invoiced once billed."
            : "The variations you've captured on site. The office will price these up and let you know when they're approved or invoiced."
        }
      />

      <div className="flex flex-wrap gap-1 mb-5" data-testid="variations-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 border-2 text-xs font-bold uppercase tracking-wider transition-all ${
              tab === t.key
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"
            }`}
            data-testid={`variations-tab-${t.key}`}
          >
            {t.label}
            {tab === t.key && items.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-[9px] bg-[#FF5F15] text-white rounded-full">
                {items.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3" data-testid="variations-loading">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-zinc-100 animate-pulse border border-zinc-200" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-300 p-12 text-center" data-testid="variations-empty">
          <FileSignature className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <div className="font-display font-bold uppercase tracking-tight text-lg">No variations here yet</div>
          <p className="text-sm text-zinc-500 mt-1 max-w-md mx-auto">
            {canEdit
              ? "When a field worker captures additional work on site, it'll appear here for you to price and approve."
              : "When the customer asks for extra work on a job, open that job and tap \u201CRequest variation\u201D \u2014 capture their signature and it lands here."}
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="variations-list">
          {items.map((v) => (
            <VariationCard
              key={v.id}
              variation={v}
              canEdit={canEdit}
              onChanged={(updated) =>
                setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
              }
              onDeleted={(id) => setItems((prev) => prev.filter((x) => x.id !== id))}
            />
          ))}
        </div>
      )}
    </div>
  );
}
